import { useEffect, useState } from 'react';
import { learningApi } from '../../api/learning';
import { useVocabularyLibrary } from '../../hooks/useVocabularyLibrary';
import { Icon } from '../../shared/Icon';
import type { VocabularyDetail, VocabularyStatus, VocabularySummary } from '../../types/api';

interface Props {
  progressVersion: number;
  onPracticeWord: (itemId: string) => Promise<void>;
  onStartNext: () => Promise<void>;
}

type Filter = 'all' | VocabularyStatus | 'due';

export function VocabularyView({ progressVersion, onPracticeWord, onStartNext }: Props) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<VocabularyDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [starting, setStarting] = useState<string | null>(null);
  const library = useVocabularyLibrary(true, query, filter, page, progressVersion);
  const data = library.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / library.limit)) : 1;

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    learningApi.vocabularyDetail(selectedId)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [selectedId, progressVersion]);

  function changeFilter(next: Filter) {
    setFilter(next);
    setPage(0);
  }

  function changeQuery(next: string) {
    setQuery(next);
    setPage(0);
  }

  async function practice(itemId: string) {
    setStarting(itemId);
    try {
      await onPracticeWord(itemId);
    } finally {
      setStarting(null);
    }
  }

  return <section className="view active">
    <header className="page-header inner-header vocabulary-header">
      <div><p className="eyebrow">3 000 VANLIGE BOKMÅLSORD</p><h1>Ordforrådet ditt,<br />med forklaring og fremgang.</h1><p className="header-copy">Søk i ord, bøyninger og ordbokforklaringer. Et riktig kontekstsvar markerer ordet som lært; repetisjon bygger gjenkalling.</p></div>
      <button className="primary-dark" onClick={() => void onStartNext()}>Fortsett å lære <Icon name="arrow" /></button>
    </header>

    {data && <div className="vocabulary-stats">
      <StatButton active={filter === 'due'} value={data.counts.due} label="klare for repetisjon" onClick={() => changeFilter('due')} />
      <StatButton active={filter === 'learning'} value={data.counts.learning} label="under innlæring" onClick={() => changeFilter('learning')} />
      <StatButton active={filter === 'learned'} value={data.counts.learned} label="lært i kontekst" onClick={() => changeFilter('learned')} />
      <StatButton active={filter === 'unseen'} value={data.counts.unseen} label="ikke startet" onClick={() => changeFilter('unseen')} />
    </div>}

    <div className="vocabulary-toolbar">
      <div className="search-box"><Icon name="search" /><input type="search" value={query} onChange={event => changeQuery(event.target.value)} placeholder="Søk i ord" aria-label="Søk etter ord, bøyning eller forklaring" /></div>
      <select value={filter} onChange={event => changeFilter(event.target.value as Filter)} aria-label="Filtrer ord etter fremgang">
        <option value="all">Alle ord{data ? ` (${data.counts.total})` : ''}</option><option value="due">Klare for repetisjon{data ? ` (${data.counts.due})` : ''}</option><option value="learning">Under innlæring{data ? ` (${data.counts.learning})` : ''}</option><option value="learned">Lært i kontekst{data ? ` (${data.counts.learned})` : ''}</option><option value="unseen">Ikke startet{data ? ` (${data.counts.unseen})` : ''}</option>
      </select>
    </div>

    {library.error && <p className="empty-state">{library.error}</p>}
    <div className={`vocabulary-list ${library.loading ? 'loading' : ''}`}>
      <div className="vocabulary-list-head"><span>ORD</span><span>FORKLARING OG EKSEMPEL</span><span>FREMGANG</span><span /></div>
      {data?.entries.map(entry => <VocabularyRow key={entry.id} entry={entry} open={selectedId === entry.id} detail={selectedId === entry.id ? detail : null} detailLoading={selectedId === entry.id && detailLoading} starting={starting === entry.id} onToggle={() => { setDetail(null); setSelectedId(selectedId === entry.id ? null : entry.id); }} onPractice={practice} />)}
    </div>
    {data && !data.entries.length && <p className="empty-state">Ingen ord matcher dette søket.</p>}
    {data && data.total > library.limit && <nav className="vocabulary-pagination" aria-label="Sider i ordbiblioteket"><button disabled={page === 0} onClick={() => setPage(value => value - 1)}>← Forrige</button><span>Side {page + 1} av {pages} · {data.total} ord</span><button disabled={page + 1 >= pages} onClick={() => setPage(value => value + 1)}>Neste →</button></nav>}
  </section>;
}

function VocabularyRow({ entry, open, detail, detailLoading, starting, onToggle, onPractice }: {
  entry: VocabularySummary;
  open: boolean;
  detail: VocabularyDetail | null;
  detailLoading: boolean;
  starting: boolean;
  onToggle: () => void;
  onPractice: (itemId: string) => Promise<void>;
}) {
  return <article className={`vocabulary-row ${open ? 'open' : ''}`}>
    <div className="vocabulary-row-main">
      <button className="vocabulary-word" onClick={onToggle} aria-expanded={open}><span className="vocabulary-rank">#{entry.rank}</span><strong>{entry.lemma}</strong><small>{entry.partsOfSpeech.join(' · ')}</small></button>
      <button className="vocabulary-explanation" onClick={onToggle}><strong>{entry.explanation ?? 'Forklaring mangler'}</strong><small>{entry.example ? `«${entry.example}»` : 'Ingen kvalitetssikret eksempel i ordboken.'}</small></button>
      <ProgressBadge entry={entry} />
      <button className="vocabulary-practice" disabled={!entry.practiceAvailable || starting} onClick={() => void onPractice(entry.id)}>{starting ? 'Laster …' : entry.progress.status === 'unseen' ? 'Lær' : 'Repeter'} <Icon name="arrow" /></button>
    </div>
    {open && <div className="vocabulary-detail">{detailLoading && <p>Laster hele ordbokartikkelen …</p>}{detail && <>
      <div className="vocabulary-forms"><span>BØYNINGER</span><p>{detail.entry.forms.join(' · ')}</p></div>
      <div className="vocabulary-senses">{detail.entry.senses.map((sense, senseIndex) => <section key={`${sense.partOfSpeech}-${senseIndex}`}><span>{sense.partOfSpeech}</span>{sense.definitions.map((definition, definitionIndex) => <div key={definitionIndex}>{definition.explanations.map(explanation => <p key={explanation}>{explanation}</p>)}{definition.examples.map(example => <blockquote key={example}>«{example}»</blockquote>)}</div>)}</section>)}</div>
    </>}</div>}
  </article>;
}

function ProgressBadge({ entry }: { entry: VocabularySummary }) {
  if (entry.progress.due) return <span className="vocabulary-status due">Repeter nå</span>;
  if (entry.progress.status === 'learned') return <span className="vocabulary-status learned"><Icon name="check" /> Lært</span>;
  if (entry.progress.status === 'learning') return <span className="vocabulary-status learning">Lærer</span>;
  return <span className="vocabulary-status unseen">Ikke startet</span>;
}

function StatButton({ active, value, label, onClick }: { active: boolean; value: number; label: string; onClick: () => void }) {
  return <button className={active ? 'active' : ''} onClick={onClick}><span className="stat-icon total"><Icon name="book" /></span><p><strong>{value}</strong><small>{label}</small></p></button>;
}
